/**
 * Where is the media in this file — if it is anywhere at all.
 *
 * ── Why this module exists ──────────────────────────────────────────────────
 * `/repair-video` used to answer two different questions with two private
 * heuristics, and both answered something other than what was asked.
 *
 *   findTopLevelMdat()   walked the box chain, then byte-scanned the FIRST
 *                        32 MB. The average file that reached that scan was
 *                        211 MB, so the scan's limit — not the file — decided
 *                        the outcome. 90 sessions in the six days after the
 *                        untrunc-side fix still bailed `no_mdat` on files
 *                        bigger than the window.
 *
 *   assessNoMediaBody()  asked "does the body look like RANDOM noise?" and
 *                        refused to say "nothing here" unless entropy was
 *                        ≥ 7.2 bits/byte. A memory card that hands back its
 *                        allocation table instead of the recording measures
 *                        3.68 — perfectly structured, entirely worthless. The
 *                        honest verdict was gated behind a test the real files
 *                        fail from the wrong side (Ksenia, 2026-08-21: 66 MB
 *                        of FAT entries, told "damage may be too severe, try a
 *                        reference clip", retried six times, gave up).
 *
 * Both are the same question — WHERE IS THE PAYLOAD — and it has an answer that
 * needs no threshold: scan the whole file for media structure and validate every
 * hit. "Nothing here" then stops being a guess about how bytes look and becomes
 * a statement about what a complete search found. That is why this module has
 * no entropy anywhere in it.
 *
 * ── What it does NOT reinvent ───────────────────────────────────────────────
 * Validation and the format table are shared, on purpose — `file-doctor` says
 * it best: one table, many consumers, or the same bytes get two stories. The
 * ISO-BMFF length/validity check is the carver's `lenIsobmff`, reached through
 * CARVE_FORMATS rather than copied, so a fix there lands here too. What is new
 * is only the search itself: the carver looks for whole files at cluster
 * boundaries across a disk image, this looks for a payload at any offset inside
 * one file — different question, different cost envelope, hand-rolled inner
 * loop instead of the carver's per-position closures.
 *
 * ── The order of preference is the point ────────────────────────────────────
 *   container → the file is INTACT, just not at byte 0. Nothing needs
 *               repairing; it needs extracting. Strictly the best outcome and
 *               the one nobody was offering.
 *   mdat      → MP4 payload without an index: the untrunc path.
 *   payload   → a payload whose OWN header is gone. Matroska Clusters are the
 *               first of these: they carry their own track numbers and
 *               timestamps, so the file needs a header DECLARED, not an index
 *               reconstructed. Until 2026-09-09 there was no anchor for them at
 *               all — the only Matroska signature here was the EBML magic, which
 *               is precisely the thing this damage destroys — so a WebM with
 *               every Cluster intact came back `none`, i.e. "there is no media
 *               in this file", and the tool disabled its own repair button
 *               (support #1622: a 519 MB .webm, 13 FFmpeg strategies, ~15 min).
 *               MP4 never had that hole because `mdat` is a payload anchor; this
 *               is the same anchor for the other family.
 *   bitstream → elementary stream, no container at all: the re-mux path.
 *   none      → the bytes PROVE there is no recording: the whole file is a few
 *               512-byte sectors repeated (zeros, erased flash, an allocation
 *               table, a card handing back one block over and over). Say so.
 *   unknown   → searched every byte, recognised nothing, proved nothing. Let the
 *               repair engine try — it knows formats this module does not.
 *
 * ⚠ Until 2026-09-25 `none` meant "no signature matched", and the signatures
 * were container headers plus the H.264 SPS. MPEG program streams (every DVD
 * .vob and most .mpg), M2TS with MPEG-2, DV, MXF, AVI bodies without their
 * RIFF header, MJPEG, HEVC and the body of any phone mp4 that lost its head
 * matched nothing — and were told, with the repair button switched off, that
 * "no program or service can get video out of this file". An absence is only
 * as good as the search behind it; the sentence was a claim about the list,
 * printed as a claim about the file. See sectorCensus for what `none` rests on
 * now, and the anchor table for the families added.
 *
 * @module media-locate
 */

import { CARVE_FORMATS } from '../disk-recovery/carver.js';
/* The Matroska payload anchor. Reached, never copied — same rule as the carver's
 * ISO-BMFF validator above: one definition, or the same bytes get two stories.
 * `matroska-surgeon` owns the EBML plumbing because it is the module that has to
 * WRITE EBML; this file only has to recognise it. */
import { clusterHere as matroskaClusterHere, clusterSpan as matroskaClusterSpan }
  from '../matroska-surgeon.js';

/** The carver's own ISO-BMFF validator — reached, never copied. It rejects the
 *  stray `....ftyp` that once carved a 774 MB "video" out of junk. */
const lenIsobmff = CARVE_FORMATS.find((f) => f.id === 'isobmff').len;

const MB = 1048576;
const SLICE = 4 * MB;
/**
 * Slice overlap. Must satisfy OVERLAP >= 19, and the arithmetic is worth writing
 * down because getting it wrong leaves a hole nothing else detects:
 *
 *   a slice inspects indices [0, n-16]  (the longest fixed look-ahead is RIFF's
 *   form at i+11), and an atom hit additionally needs the 4 SIZE bytes in front,
 *   so a slice VALIDLY covers [base+4, base+SLICE-16]. The next slice starts at
 *   base+SLICE-OVERLAP and validly begins at base+SLICE-OVERLAP+4. Contiguity
 *   therefore needs -OVERLAP+4 <= -15, i.e. OVERLAP >= 19.
 *
 * At 16 there was a three-byte blind spot per seam: a real `mdat` box header
 * planted at SLICE-17 was missed outright while its neighbours at SLICE-16 and
 * beyond were found. 32 leaves headroom and costs 32 bytes per 4 MB read.
 */
const OVERLAP = 32;

/**
 * `complete` is a THREE-state answer, and the third state is why this constant
 * exists rather than a bare `false`.
 *
 *   true     the box chain was walked and it ends inside the file — measured.
 *   false    the chain was walked and it runs past EOF — measured truncation.
 *   UNKNOWN  we found this container by its signature and never asked. Every
 *            format below MP4 lands here: a Matroska/RIFF/Ogg/FLV/ASF/TS hit is
 *            four validated bytes at an offset, and nothing in that tells us
 *            whether the tail survived.
 *
 * It used to be `complete: at === 0` for all seven of those formats, which reads
 * as an answer and is a restatement of the question — and it was wrong in both
 * directions. A truncated WebM at byte 0 came back `complete: true`; a whole,
 * byte-perfect WebM behind a zeroed cluster came back `complete: false`. The
 * only consumer (repair-video's extract-at-offset lane) gates on this flag, so
 * `at > 0 && complete` was unsatisfiable for every format except MP4: the lane
 * the module's own docblock calls "strictly the best outcome" fired 7 times in
 * 90 days, all seven mp4, while whole AVI/MKV/WebM files were sent down a
 * ladder of FFmpeg passes that cannot open a file whose byte 0 is not a header.
 * (Support #1622, 2026-09-09: a 519 MB WebM, 13 strategies, ~15 minutes, "не
 * удалось восстановить файл".)
 *
 * `null` rather than `false` because a consumer must be able to tell "we looked
 * and it is cut" from "nobody looked" — the first is a reason to hesitate, the
 * second is a reason to ask the verifier.
 */
const UNKNOWN = null;

const u32be = (b, o) => ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;

/** A reader over a File/Blob, so callers may pass either. */
function toReader(file) {
  return {
    size: file.size,
    read: async (at, len) =>
      new Uint8Array(await file.slice(at, Math.min(at + len, file.size)).arrayBuffer()),
  };
}

/* ── tier 1: the box chain ────────────────────────────────────────────────────
 * Header-only walk: 16 bytes read per box, then jump by the declared size. An
 * intact chain answers in O(#boxes) no matter how large the file or how far in
 * the payload sits — which is why the tier-2 scan below is never paid for by
 * the healthy majority. Recorders that reserve index space up front
 * ([ftyp][free …MB][mdat] — Zoom's converter, CCTV writers) are found here.
 */
async function walkBoxes(r, from) {
  let pos = from || 0, hops = 0;
  const seen = [];
  let mdat = null, ftypAt = -1, truncated = false;
  while (pos + 8 <= r.size && hops++ < 4096) {
    const h = await r.read(pos, 16);
    if (h.length < 8) break;
    let size = u32be(h, 0);
    const type = String.fromCharCode(h[4], h[5], h[6], h[7]);
    if (!/^[\x20-\x7E]{4}$/.test(type)) break;      // not a box chain at all
    let hdr = 8;
    if (size === 1) {
      if (h.length < 16) break;
      size = u32be(h, 8) * 4294967296 + u32be(h, 12);
      hdr = 16;
    } else if (size === 0) {
      size = r.size - pos;                          // "to end of file" — legal, and last
    }
    seen.push(type);
    if (type === 'ftyp' && ftypAt < 0) ftypAt = pos;
    if (type === 'mdat' && !mdat) mdat = { at: pos + hdr, bytes: Math.max(0, Math.min(size - hdr, r.size - pos - hdr)) };
    if (size < hdr) break;                          // corrupt size — chain unusable
    if (pos + size > r.size) { truncated = true; break; }
    pos += size;
  }
  /* `moof` counts: a fragmented file indexes each fragment in place and needs no
   * `moov` to be playable. Treating it as index-less would send perfectly good
   * DASH/fMP4 recordings down the reconstruction path. */
  const hasIndex = seen.indexOf('moov') >= 0 || seen.indexOf('moof') >= 0;
  return { seen, mdat, ftypAt, hasIndex, truncated };
}

/* ── tier 2: whole-file signature scan ────────────────────────────────────────
 * Only reached when the chain above is unusable from byte 0 — a carve, an
 * undelete, a half-overwritten cluster. Cost is one sequential pass in 4 MB
 * slices with nothing retained between them: the same I/O the repair itself is
 * about to do, which is what makes buying the file back worth it.
 *
 * The inner loop is written out rather than driven by a signature table because
 * it runs once per byte of a file that averages 211 MB: one load and up to four
 * compares, no closure per position.
 */
/* ── signature validators ─────────────────────────────────────────────────────
 * A signature match is a hypothesis, never a finding. Over a file that averages
 * 211 MB an unvalidated 4-byte pattern is expected to hit by chance, and a
 * 3-byte one hits several times — measured, not assumed: bare `ID3` matched 64 MB
 * of random bytes at offset 5822844 and reported it as an MP3. Each validator
 * below reads a few more bytes and demands something junk cannot supply, which
 * is the same discipline that stops the carver turning a stray `ftyp` into a
 * 774 MB "video".
 */

/**
 * EBML is only four bytes, so something behind it must corroborate — but the
 * corroboration cannot assume the header SURVIVED, because a broken EBML header
 * is precisely what this tool is handed. `matrix-8-mkv_ebml_broken.mkv` keeps an
 * intact `1A 45 DF A3`, garbage where its DocType should be, and a perfectly
 * good Segment element at 0x28; demanding the DocType declared that file empty.
 */
function okEbml(b, i) {
  const end = Math.min(b.length, i + 256);
  for (let k = i + 4; k + 4 < end; k++) {
    if (b[k] === 0x6D && b[k + 1] === 0x61 && b[k + 2] === 0x74 && b[k + 3] === 0x72) return true; // "matr"
    if (b[k] === 0x77 && b[k + 1] === 0x65 && b[k + 2] === 0x62 && b[k + 3] === 0x6D) return true; // "webm"
    // Segment ID — the element every Matroska file has and damage rarely reaches.
    if (b[k] === 0x18 && b[k + 1] === 0x53 && b[k + 2] === 0x80 && b[k + 3] === 0x67) return true;
  }
  return false;
}

/** ID3v2: a real header carries a version and a synchsafe size (every byte < 0x80). */
function okId3(b, i) {
  const major = b[i + 3];
  if (major !== 2 && major !== 3 && major !== 4) return false;
  if (b[i + 4] === 0xFF) return false;
  return b[i + 6] < 0x80 && b[i + 7] < 0x80 && b[i + 8] < 0x80 && b[i + 9] < 0x80;
}

/** FLAC always opens with a STREAMINFO block, and STREAMINFO is always 34 bytes. */
function okFlac(b, i) {
  if ((b[i + 4] & 0x7F) !== 0) return false;
  return ((b[i + 5] << 16) | (b[i + 6] << 8) | b[i + 7]) === 34;
}

/** Ogg page: version must be 0 and the header-type flags use only three bits. */
const okOgg = (b, i) => b[i + 4] === 0 && (b[i + 5] & 0xF8) === 0;

/** FLV: version 1, only two defined flag bits, and a header that is 9 bytes. */
const okFlv = (b, i) =>
  (b[i + 4] & 0xFA) === 0 && ((b[i + 5] << 24) | (b[i + 6] << 16) | (b[i + 7] << 8) | b[i + 8]) === 9;

/**
 * Anchor table: one array lookup replaces a chain of eight byte comparisons.
 *
 * Proving a file empty means reading every byte of it, so the per-byte cost IS
 * the runtime — and 97% of those bytes match nothing. On an idle machine the
 * if/else-if chain ran at 59 MB/s (17.4 s to clear a 1 GB file) and the table at
 * 125 MB/s (8.2 s); adding the TS/SPS anchors costs about 11% on top, measured
 * as a same-run ratio against an all-zero buffer in which no anchor byte exists
 * (16 vs 18 MB/s while a neighbouring session had the box ~18x slowed). Call it
 * ~110 MB/s idle: roughly 2 s to prove the 211 MB cohort average empty, against
 * the seven minutes of failing repairs it replaces.
 *
 * Absolute figures here are idle-machine numbers. The RATIO is what survives a
 * loaded box, and it is the ratio that says whether a change was worth it —
 * a wall-clock number measured under someone else's workload says nothing.
 */
const A_FTYP = 1, A_MDAT = 2, A_EBML = 3, A_RIFF = 4, A_OGG = 5, A_ID3 = 6, A_FLV = 7, A_ASF = 8,
      A_SYNC = 9, A_CLUSTER = 10, A_START = 11, A_MXF = 12, A_FF = 13;

/** RIFF types worth claiming. A known form is a far narrower target than four
 *  printable bytes, which is what lets the size check go. */
const RIFF_FORMS = { 'AVI ': 'avi', AVIX: 'avi', WAVE: 'wav', WEBP: 'webp', RMID: 'midi', ACON: 'ani', CDDA: 'cda' };
const ANCHOR = new Uint8Array(256);
ANCHOR[0x66] = A_FTYP;   // 'f' — ftyp, and fLaC shares the anchor
ANCHOR[0x6D] = A_MDAT;   // 'm'
ANCHOR[0x1A] = A_EBML;   // EBML
ANCHOR[0x52] = A_RIFF;   // 'R'
ANCHOR[0x4F] = A_OGG;    // 'O'
ANCHOR[0x49] = A_ID3;    // 'I'
ANCHOR[0x46] = A_FLV;    // 'F'
ANCHOR[0x30] = A_ASF;    // ASF GUID first byte
/* MPEG-TS sync AND an H.264 parameter-set header share these bytes, and both are
 * checked in one branch. They are here rather than in a sampled probe because
 * sampling has holes and the holes are where the damaged files live:
 * `matrix-11-mpeg2_ts_damaged.mts` carries 7 340 032 zero bytes — 93.7 % of the
 * file — before its first sync byte, so windows at 0 %, 25 % and 50 % all landed
 * in the zeros and the file was declared empty. A full pass has no such holes. */
ANCHOR[0x1F] = A_CLUSTER; // Matroska Cluster — and the DV DIF header block, which shares the byte
ANCHOR[0x47] = A_SYNC;   // TS sync, and SPS with nal_ref_idc=2
ANCHOR[0x67] = A_SYNC;   // SPS with nal_ref_idc=3 (what encoders actually emit)
ANCHOR[0x27] = A_SYNC;   // SPS with nal_ref_idc=1
/* ── The families this table did not have until 2026-09-25 ────────────────────
 * Everything above is either a container header or an H.264 parameter set, and
 * `none` was printed whenever none of them matched — as "there is no video in
 * this file, no program can get it out". Measured that day on the real page and
 * on prod: a HEALTHY DVD .vob, a healthy .mpg (MPEG program stream), an M2TS
 * with MPEG-2 inside, a raw .dv and an .mxf were each told exactly that, with
 * the repair button switched off. On damaged files — the only files this tool
 * sees — it was worse: with 64 KB of the head gone, an mp4, a mov, an mkv, an
 * MJPEG or Xvid AVI and an FLV were all "empty", although 99% of their frames
 * were still in the file. RU, 09-09…09-25: `none` for 13 of 15 VOB sessions,
 * 15 of 20 MPG, 22 of 28 TS and 105 of 174 AVI, against ~24% for MP4/MOV —
 * the formats with no anchor here were declared empty almost wholesale.
 *
 * Each new anchor is validated the way the old ones are: a program-stream pack
 * by its marker bits AND the start code its own length points at; an MPEG-1/2
 * or MPEG-4 Part 2 stream by a PARSED picture size; DV by the fixed block order
 * of a DIF sequence; MXF by a second KLV key where the first one's BER length
 * lands; JPEG by a segment walk that reaches SOS through a sane SOF; an AVI
 * body by a chain of chunks. None of them can be passed by a run of junk. */
ANCHOR[0x01] = A_START;  // 00 00 01 xx — MPEG program stream, MPEG-1/2/4 video, HEVC
ANCHOR[0x06] = A_MXF;    // SMPTE KLV key 06 0E 2B 34 — MXF (pro camcorders)
ANCHOR[0xFF] = A_FF;     // FF D8 FF — JPEG frames: MJPEG AVI/MOV off dashcams and old cameras

/* ── A payload that lost its box header: length-prefixed NAL units ────────────
 * An MP4/MOV `mdat` is a chain of [u32 length][NAL] records. Phones keep their
 * parameter sets in the moov's avcC/hvcC only, so when the head of such a file
 * is gone there is no SPS anywhere and no `mdat` word either — and every one of
 * those files was reported empty, although every frame was still in it. The
 * chain itself is the evidence: four consecutive records whose lengths land
 * exactly on the next plausible NAL header is a structure junk does not make
 * (per link ≈ 1/256 for the zero high byte × ~0.12 for a valid header, so four
 * links ≈ 3·10⁻¹⁵ per position; the allocation-table shape that fooled the old
 * Annex-B count breaks at the second link).
 *
 * The chain is entered only at a SLICE header (the ten bytes below are the
 * slice headers phones and cameras actually write — H.264 P/IDR at every
 * nal_ref_idc, HEVC TRAIL_R and the IRAP types) and walked from there through
 * whatever NAL types follow. A chunk of a dozen samples offers a dozen entry
 * points, so a narrow entry set costs no recall; a wide one cost 68% of scan
 * speed on random bytes (measured: 27 lead bytes, 1.9 → 3.2 s per 128 MB), since
 * every entry is a branch the CPU cannot predict.
 * They share the anchor table (code A_NAL), so the common case — a byte that
 * is nothing — still costs exactly one lookup. */
const A_NAL = 14;
for (const v of [0x21, 0x41, 0x61, 0x25, 0x45, 0x65,     // H.264 slice / IDR
                 0x02, 0x26, 0x28, 0x2A]) {                // HEVC TRAIL_R, IDR_W_RADL, IDR_N_LP, CRA
  if (!ANCHOR[v]) ANCHOR[v] = A_NAL;
}
const H264_TYPE_OK = new Uint8Array(32);
for (const t of [1, 2, 3, 4, 5, 6, 7, 8, 9, 12]) H264_TYPE_OK[t] = 1;
const HEVC_TYPE_OK = new Uint8Array(64);
for (const t of [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 16, 17, 18, 19, 20, 21, 32, 33, 34, 35, 36, 37, 38, 39, 40]) HEVC_TYPE_OK[t] = 1;

/** Stream ids a program stream may put after a start code (ISO 13818-1 table 2-18). */
const PS_ID = new Uint8Array(256);
PS_ID[0xB9] = PS_ID[0xBA] = PS_ID[0xBB] = 1;
for (let c = 0xBC; c <= 0xFF; c++) PS_ID[c] = 1;

const isStart = (b, q) => b[q] === 0 && b[q + 1] === 0 && b[q + 2] === 1;
/** Transport-stream packet sizes: plain, BDAV/M2TS (AVCHD), DVB with parity. */
const TS_PERIODS = [188, 192, 204];

/**
 * Do the packets from `i` (period P) carry continuity counters that COUNT?
 * For each PID the 4-bit counter of a payload-carrying packet is the previous
 * one + 1 (ISO 13818-1 2.4.3.3). Up to 16 packets are read; a stream offers
 * several same-PID pairs among them, junk offers pairs that stand still. One
 * miss is tolerated — a single damaged packet is the damage this tool repairs.
 */
function tsCountsUp(b, i, P, n) {
  const lastCc = new Map();
  let pairs = 0, good = 0;
  for (let k = 0; k < 16; k++) {
    const q = i + k * P;
    if (q + 4 > n || b[q] !== 0x47) break;
    const pid = ((b[q + 1] & 0x1F) << 8) | b[q + 2];
    if (pid === 0x1FFF || !(b[q + 3] & 0x10)) continue;   // null packet / no payload
    const cc = b[q + 3] & 0x0F;
    if (lastCc.has(pid)) { pairs++; if (cc === ((lastCc.get(pid) + 1) & 15)) good++; }
    lastCc.set(pid, cc);
  }
  return pairs >= 2 && good >= pairs - 1 && good >= 2;
}

/**
 * MPEG program-stream pack header at `s` (the first 00 of 00 00 01 BA), MPEG-2 or
 * MPEG-1, confirmed by the start code sitting exactly where the header's own
 * length ends. DVD .vob, .mpg, JVC .mod/.tod-style camcorder files, .vro.
 */
function psPackAt(b, s, n) {
  if (s + 16 > n) return false;
  const b4 = b[s + 4];
  let hl;
  if ((b4 & 0xC4) === 0x44) {                               // MPEG-2: '01' + marker bits
    if ((b[s + 6] & 0x04) !== 0x04 || (b[s + 8] & 0x04) !== 0x04
        || (b[s + 9] & 0x01) !== 0x01 || (b[s + 12] & 0x03) !== 0x03) return false;
    if (!((b[s + 10] << 14) | (b[s + 11] << 6) | (b[s + 12] >> 2))) return false;  // program_mux_rate
    hl = 14 + (b[s + 13] & 0x07);
  } else if ((b4 & 0xF1) === 0x21) {                        // MPEG-1: '0010' + marker bits
    if ((b[s + 6] & 0x01) !== 1 || (b[s + 8] & 0x01) !== 1
        || (b[s + 9] & 0x80) !== 0x80 || (b[s + 11] & 0x01) !== 1) return false;
    hl = 12;
  } else return false;
  const q = s + hl;
  return q + 4 <= n && isStart(b, q) && PS_ID[b[q + 3]] === 1;
}

/** A PES packet at `s`, confirmed by the next start code at the packet's own end.
 *  Catches a program stream whose pack headers are gone. (Inside a transport
 *  stream a PES spans packets, so this never fires there — the sync anchor does.) */
function pesAt(b, s, n) {
  if (s + 9 > n) return false;
  const L = (b[s + 4] << 8) | b[s + 5];
  if (L < 3) return false;
  const f = b[s + 6];
  const mpeg2 = (f & 0xC0) === 0x80;
  const mpeg1 = f === 0xFF || (f & 0xC0) === 0x40 || (f & 0xE0) === 0x20 || f === 0x0F;
  if (!mpeg2 && !mpeg1) return false;
  if (mpeg2 && b[s + 8] + 3 > L) return false;              // PES_header_data_length must fit
  const q = s + 6 + L;
  return q + 4 <= n && isStart(b, q) && PS_ID[b[q + 3]] === 1;
}

/** MPEG-1/2 video sequence header at `s` — claimed on a parsed picture size and a
 *  following start code, the same discipline as the H.264 SPS parse. */
function mpegSeqAt(b, s, n) {
  if (s + 16 > n) return null;
  const w = (b[s + 4] << 4) | (b[s + 5] >> 4), h = ((b[s + 5] & 0x0F) << 8) | b[s + 6];
  const ar = b[s + 7] >> 4, fr = b[s + 7] & 0x0F;
  if (w < 16 || h < 16 || w > 4096 || h > 4096 || ar < 1 || ar > 14 || fr < 1 || fr > 8) return null;
  if (((b[s + 10] >> 5) & 1) !== 1) return null;           // marker_bit
  if (!((b[s + 8] << 10) | (b[s + 9] << 2) | (b[s + 10] >> 6))) return null;  // bit_rate_value
  const end = Math.min(n - 4, s + 300);
  for (let q = s + 12; q <= end; q++) {
    if (isStart(b, q)) {
      const c = b[q + 3];
      return (c === 0xB5 || c === 0xB8 || c === 0x00 || c === 0xB2 || c === 0xB3) ? { width: w, height: h } : null;
    }
  }
  return null;
}

/** Plain MSB-first bit reader (MPEG-4 Part 2 has no emulation prevention to strip). */
function bitReader(b, from, end) {
  let bit = from * 8;
  const u = (k) => {
    let v = 0;
    for (let i = 0; i < k; i++) {
      const byte = bit >> 3;
      if (byte >= end) throw new RangeError('eof');
      v = (v * 2) + ((b[byte] >> (7 - (bit & 7))) & 1);
      bit++;
    }
    return v;
  };
  return { u };
}

/** MPEG-4 Part 2 VideoObjectLayer at `s` (00 00 01 2x): parsed through its marker
 *  bits to the picture size. DivX/Xvid AVI, old phone .3gp/.mp4, cameras. */
function m4vVolAt(b, s, n) {
  try {
    const br = bitReader(b, s + 4, Math.min(n, s + 64));
    br.u(1);                                                // random_accessible_vol
    const vot = br.u(8);
    if (vot < 1 || vot > 0x12) return null;
    let verid = 1;
    if (br.u(1)) { verid = br.u(4); br.u(3); if ([1, 2, 4, 5].indexOf(verid) < 0) return null; }
    const ar = br.u(4);
    if (ar === 0 || (ar > 5 && ar !== 15)) return null;
    if (ar === 15 && (!br.u(8) || !br.u(8))) return null;
    if (br.u(1)) {                                          // vol_control_parameters
      if (br.u(2) !== 1) return null;                      // chroma_format 4:2:0
      br.u(1);
      if (br.u(1)) {                                        // vbv_parameters
        br.u(15); if (!br.u(1)) return null; br.u(15); if (!br.u(1)) return null;
        br.u(15); if (!br.u(1)) return null; br.u(3); br.u(11); if (!br.u(1)) return null;
        br.u(15); if (!br.u(1)) return null;
      }
    }
    const shape = br.u(2);
    if (shape !== 0) return null;                           // rectangular — every camera
    if (!br.u(1)) return null;                              // marker
    const res = br.u(16);
    if (!res || !br.u(1)) return null;
    if (br.u(1)) { let nb = 1; while ((1 << nb) < res) nb++; br.u(nb); }
    if (!br.u(1)) return null;
    const w = br.u(13);
    if (!br.u(1)) return null;
    const h = br.u(13);
    if (!br.u(1)) return null;
    if (w < 16 || h < 16 || w > 4096 || h > 4096) return null;
    return { width: w, height: h };
  } catch (_) { return null; }
}

/** HEVC Annex-B: a VPS followed, within a few hundred bytes, by SPS and PPS —
 *  the parameter-set triple an HEVC stream repeats at every random-access point. */
function hevcParamSetsAt(b, s, n) {
  const end = Math.min(n - 5, s + 600);
  let sps = false;
  for (let q = s + 5; q <= end; q++) {
    if (!isStart(b, q)) continue;
    if (!sps && b[q + 3] === 0x42 && b[q + 4] === 0x01) sps = true;
    else if (sps && b[q + 3] === 0x44 && b[q + 4] === 0x01) return true;
  }
  return false;
}

/** DV (IEC 61834 / SMPTE 314M): the header block of a DIF sequence at `i`, then the
 *  block order every sequence has — subcode ×2, VAUX ×3, audio, video. */
function dvAt(b, i, n) {
  if (i + 640 > n) return false;
  const want = [[0x20, 0], [0x20, 1], [0x40, 0], [0x40, 1], [0x40, 2], [0x60, 0], [0x80, 0]];
  for (let k = 0; k < want.length; k++) {
    const q = i + 80 * (k + 1);
    if ((b[q] & 0xE0) !== want[k][0] || b[q + 1] !== b[i + 1] || b[q + 2] !== want[k][1]) return false;
  }
  return true;
}

/** MXF: a SMPTE KLV key at `i` and a second key exactly where its BER length ends. */
function mxfAt(b, i, n) {
  if (i + 20 > n) return false;
  const cat = b[i + 4];
  if (cat < 1 || cat > 4 || b[i + 5] === 0 || b[i + 5] > 0x7F) return false;
  let q = i + 16, len;
  const l0 = b[q];
  if (l0 < 0x80) { len = l0; q += 1; }
  else {
    const nb = l0 & 0x7F;
    if (nb < 1 || nb > 8 || q + 1 + nb > n) return false;
    len = 0;
    for (let k = 0; k < nb; k++) len = len * 256 + b[q + 1 + k];
    q += 1 + nb;
  }
  const next = q + len;
  return next + 4 <= n && b[next] === 0x06 && b[next + 1] === 0x0E && b[next + 2] === 0x2B && b[next + 3] === 0x34;
}

const TWOCC_AV = { dc: 2, db: 2, __: 2, wb: 1, pc: 1, tx: 1 };   // '__' — DV type 1 ('iavs'): audio rides inside the frames
/**
 * The body of an AVI whose RIFF header is gone: a chain of `##dc` / `##wb`
 * chunks, each at the offset the previous one's size points to. Dashcams and
 * older cameras write MJPEG or MPEG-4 AVI; losing the first cluster takes the
 * header with it, and those files were being called empty with every frame left.
 */
function aviChunksAt(b, i, n) {
  let p = i, links = 0, video = 0;
  while (p + 12 <= n && links < 8) {
    const c0 = b[p], c1 = b[p + 1];
    let isList = false, ok = 0;
    if (c0 >= 0x30 && c0 <= 0x39 && c1 >= 0x30 && c1 <= 0x39) {
      ok = TWOCC_AV[String.fromCharCode(b[p + 2], b[p + 3])] || 0;
    } else {
      const id = String.fromCharCode(c0, c1, b[p + 2], b[p + 3]);
      if (id === 'JUNK' || id === 'idx1' || /^ix\d\d$/.test(id)) ok = 1;
      else if (id === 'LIST') { ok = 1; isList = true; }
    }
    if (!ok) break;
    const sz = (b[p + 4] | (b[p + 5] << 8) | (b[p + 6] << 16) | (b[p + 7] << 24)) >>> 0;
    if (sz > 16 * MB) break;
    links++;
    if (ok === 2) video++;
    const lt = isList ? String.fromCharCode(b[p + 8], b[p + 9], b[p + 10], b[p + 11]) : '';
    p += (lt === 'movi' || lt === 'rec ') ? 12 : 8 + sz + (sz & 1);
  }
  return links >= 3 && video >= 1;
}

/** A JPEG image at `i` (FF D8 FF): segments walked by their own lengths until SOS,
 *  through a SOF whose precision, size and component count are sane. */
function jpegAt(b, i, n) {
  const end = Math.min(n, i + 65536);
  let p = i + 2, sof = null;
  for (let segs = 0; segs < 48 && p + 4 <= end; segs++) {
    if (b[p] !== 0xFF) return null;
    let m = b[p + 1];
    while (m === 0xFF && p + 2 < end) { p++; m = b[p + 1]; }   // fill bytes
    if (m === 0x00 || m === 0xD8 || m === 0xD9) return null;
    if (m === 0x01 || (m >= 0xD0 && m <= 0xD7)) { p += 2; continue; }
    const len = (b[p + 2] << 8) | b[p + 3];
    if (len < 2) return null;
    if (m === 0xDA) return sof;
    if (m >= 0xC0 && m <= 0xCF && m !== 0xC4 && m !== 0xC8 && m !== 0xCC) {
      if (p + 10 > end) return null;
      const prec = b[p + 4], hgt = (b[p + 5] << 8) | b[p + 6], wid = (b[p + 7] << 8) | b[p + 8], nc = b[p + 9];
      if ((prec !== 8 && prec !== 12) || !wid || wid > 16384 || hgt > 16384
          || (nc !== 1 && nc !== 3 && nc !== 4) || len !== 8 + 3 * nc) return null;
      sof = { width: wid, height: hgt };
    }
    p += 2 + len;
  }
  return null;
}

/** Walk [u32 length][NAL] records from `s` reading headers as H.264 or HEVC.
 *  The records must not all be the same length: a block repeated at a fixed
 *  period with a plausible length word in it would otherwise "chain" onto its
 *  own copies — compressed frames never come out byte-identical in size. */
function avccWalk(b, s, n, hevc) {
  let p = s, links = 0, vcl = 0, firstL = -1, varied = false;
  while (p + 6 <= n && links < 16) {
    if (b[p] !== 0) break;
    const L = (b[p + 1] << 16) | (b[p + 2] << 8) | b[p + 3];
    if (L < 2 || L > 0x7FFFFF) break;
    if (firstL < 0) firstL = L; else if (L !== firstL) varied = true;
    const h0 = b[p + 4];
    if (h0 & 0x80) break;                                   // forbidden_zero_bit
    if (!hevc) {
      const t = h0 & 0x1F, ref = h0 >> 5;
      if (!H264_TYPE_OK[t] || (t === 5 && !ref) || ((t === 6 || t === 9 || t === 12) && ref)) break;
      if (t <= 5) vcl++;
    } else {
      const h1 = b[p + 5], t = h0 >> 1;
      if ((h0 & 1) || (h1 >> 3) || !(h1 & 7) || !HEVC_TYPE_OK[t]) break;
      if (t <= 21) vcl++;
    }
    links++;
    p += 4 + L;
  }
  return links >= 4 && vcl >= 2 && varied ? { links, vcl } : null;
}

async function scanSignatures(r, opts) {
  const onProgress = opts.onProgress, signal = opts.signal;
  /* An ELEMENTARY-stream hit (an H.264/HEVC parameter set, an MPEG sequence
   * header or VOL, a JPEG) is held, not returned: the same bytes live inside
   * container METADATA — a cover picture in `moov/udta`, the VOL in an mp4v
   * `esds`, a thumbnail — and they come BEFORE the `mdat` that is the real
   * answer. Measured 2026-09-25 on a faststart mp4 with a cover and on an mp4v
   * mp4, both with ftyp wiped: the verdict read `bitstream` at the metadata
   * instead of `mdat`, and the page's mdat strategies lost their offset. A
   * container or payload anchor still answers at once; a held stream is the
   * answer only when the whole pass found nothing stronger. */
  let loose = null;
  for (let base = 0; base < r.size; base += SLICE - OVERLAP) {
    if (signal && signal.aborted) return null;
    const buf = await r.read(base, SLICE);
    const n = buf.length;
    if (n < 16) break;
    for (let i = 0; i + 16 <= n; i++) {
      const a = ANCHOR[buf[i]];
      if (a === 0) continue;
      if (a === A_NAL) {
        /* A slice header behind a 4-byte length: maybe the body of an mp4/mov
         * that lost its head (see A_NAL). The zero high byte rejects 255/256. */
        if (i < 4 || buf[i - 4] !== 0) continue;
        const s = i - 4;
        const h = avccWalk(buf, s, n, false) ? 'h264' : (avccWalk(buf, s, n, true) ? 'hevc' : '');
        if (h) {
          const at = base + s;
          return { kind: 'payload', format: 'avcc', codec: h, at, bytes: r.size - at, complete: false };
        }
        continue;
      }
      if (a === A_FTYP) {                                   // 'f' → ftyp or fLaC
        if (buf[i + 1] === 0x4C && buf[i + 2] === 0x61 && buf[i + 3] === 0x43 && okFlac(buf, i)) {
          const at = base + i;
          return { kind: 'container', format: 'flac', at, bytes: r.size - at, complete: UNKNOWN };
        }
        if (buf[i + 1] !== 0x74 || buf[i + 2] !== 0x79 || buf[i + 3] !== 0x70) continue;
        /* ftyp lives at offset 4 of the file, behind its box size. */
        if (i < 4) continue;
        const at = base + i - 4;
        const len = await lenIsobmff(r.read, at, r.size);   // shared validator
        if (len > 0) {
          /* Classified by the same walk tier 1 uses, so a file found at 40 MB is
           * described exactly as the same file would be at byte 0 — an offset
           * must not change the diagnosis. */
          const w = await walkBoxes(r, at);
          if (w.hasIndex) return { kind: 'container', format: 'mp4', at, bytes: len,
                                   complete: !w.truncated, atoms: w.seen,
                                   mdatAt: w.mdat ? w.mdat.at : -1,
                                   mdatBytes: w.mdat ? w.mdat.bytes : 0 };
          if (w.mdat) return { kind: 'mdat', format: 'mp4', at: w.mdat.at,
                               bytes: w.mdat.bytes, complete: false, atoms: w.seen };
        }
      } else if (a === A_MDAT) {                            // 'm' → mdat
        if (buf[i + 1] !== 0x64 || buf[i + 2] !== 0x61 || buf[i + 3] !== 0x74) continue;
        if (i < 4) continue;   // size bytes must be present; the seam is covered
                               // by the previous slice (see OVERLAP)
        const at = base + i - 4;
        const declared = u32be(buf, i - 4);
        /* `mdat` also occurs inside compressed video data, so the size field in
         * front of it is what separates a real box header from a coincidence.
         * 0 = "runs to EOF" (a recorder killed before finalising — exactly this
         * cohort), 1 = a 64-bit size follows, otherwise it must fit. */
        if (declared !== 0 && declared !== 1 && !(declared >= 8 && at + declared <= r.size)) continue;
        const dataAt = at + (declared === 1 ? 16 : 8);
        return { kind: 'mdat', format: 'mp4', at: dataAt, bytes: r.size - dataAt, complete: false };
      } else if (a === A_EBML) {                            // EBML → Matroska/WebM
        if (buf[i + 1] === 0x45 && buf[i + 2] === 0xDF && buf[i + 3] === 0xA3 && okEbml(buf, i)) {
          const at = base + i;
          return { kind: 'container', format: 'matroska', at, bytes: r.size - at, complete: UNKNOWN };
        }
      } else if (a === A_CLUSTER) {                         // Matroska Cluster → payload
        /* Reached only when no EBML header was found EARLIER in the file: the
         * scan runs forward and a healthy Matroska puts its header in front of
         * its first Cluster, so an intact file still answers `container`.
         *
         * Four anchor bytes are a hypothesis (the rule this module was built on),
         * and `clusterHere` already demands a parseable size and a child element
         * a Cluster actually contains. One more corroboration on top: a SECOND
         * Cluster exactly where the first one's size says it will be. A SeekHead
         * stores this very ID as the PAYLOAD of a SeekID element, so a lone hit
         * inside surviving metadata is a real thing to guard against, and a chain
         * is what metadata cannot fake. */
        /* The three bytes behind the anchor are compared INLINE before the
         * validator is called, exactly as the ftyp/mdat branches do. Calling
         * across the module boundary on every 0x1F byte — one in 256 of random
         * data — made a noise-filled file scan 2.40× slower (measured); the
         * inline reject brings that to 1.00×, because junk essentially never
         * carries the full four. */
        if (buf[i + 1] === 0x43 && buf[i + 2] === 0xB6 && buf[i + 3] === 0x75 &&
            matroskaClusterHere(buf, i)) {
          const sz = matroskaClusterSpan(buf, i);
          const chained = sz > 0 && i + sz + 16 <= n ? matroskaClusterHere(buf, i + sz)
                        : sz > 0 && base + i + sz >= r.size - 16;   // last cluster runs to EOF
          if (chained) {
            const at = base + i;
            return { kind: 'payload', format: 'matroska', at, bytes: r.size - at, complete: UNKNOWN };
          }
        } else if (buf[i + 1] === 0x07 && buf[i + 2] === 0x00 && (buf[i + 3] & 0x7F) === 0x3F
                   && dvAt(buf, i, n)) {
          /* DV: the same first byte opens the header block of every DIF
           * sequence (MiniDV camcorders, .dv captures). */
          const at = base + i;
          return { kind: 'container', format: 'dv', at, bytes: r.size - at, complete: UNKNOWN };
        }
      } else if (a === A_RIFF) {                            // 'R' → RIFF
        /* Two lessons live in this branch, both from files that were called
         * empty and were not:
         *
         *   Any RIFF FORM, not just `AVI ` — an 88 KB WAV wearing a .mp4
         *   extension was reported as holding no media at all.
         *
         *   No ceiling on the declared size. A RIFF whose size field says more
         *   than the file holds is not a false hit, it is the classic sign of a
         *   recording that stopped before its header was rewritten — `probeRiff`
         *   says so in as many words, and `matrix-9-avi_truncated.avi` (declares
         *   742 884 B, holds 445 730) is exactly that file. Requiring the size to
         *   fit rejected the damage this tool exists to repair.
         *
         * Strength comes from the form instead: a KNOWN four-character type is a
         * far narrower target than "four printable bytes". */
        if (buf[i + 1] === 0x49 && buf[i + 2] === 0x46 && buf[i + 3] === 0x46) {
          const form = String.fromCharCode(buf[i + 8], buf[i + 9], buf[i + 10], buf[i + 11]);
          const fmt = RIFF_FORMS[form];
          if (fmt) {
            const at = base + i;
            return { kind: 'container', format: fmt, at, bytes: r.size - at, complete: UNKNOWN };
          }
        }
      } else if (a === A_OGG) {                             // 'O' → OggS
        if (buf[i + 1] === 0x67 && buf[i + 2] === 0x67 && buf[i + 3] === 0x53 && okOgg(buf, i)) {
          const at = base + i;
          return { kind: 'container', format: 'ogg', at, bytes: r.size - at, complete: UNKNOWN };
        }
      } else if (a === A_ID3) {                             // 'I' → ID3 (MP3 with tag)
        if (buf[i + 1] === 0x44 && buf[i + 2] === 0x33 && okId3(buf, i)) {
          const at = base + i;
          return { kind: 'container', format: 'mp3', at, bytes: r.size - at, complete: UNKNOWN };
        }
      } else if (a === A_FLV) {                             // 'F' → FLV
        if (buf[i + 1] === 0x4C && buf[i + 2] === 0x56 && buf[i + 3] === 0x01 && okFlv(buf, i)) {
          const at = base + i;
          return { kind: 'container', format: 'flv', at, bytes: r.size - at, complete: UNKNOWN };
        }
      } else if (a === A_ASF) {                             // ASF/WMV GUID (16 bytes — strong on its own)
        if (buf[i + 1] === 0x26 && buf[i + 2] === 0xB2 && buf[i + 3] === 0x75 &&
            buf[i + 4] === 0x8E && buf[i + 5] === 0x66 && buf[i + 6] === 0xCF && buf[i + 7] === 0x11) {
          const at = base + i;
          return { kind: 'container', format: 'asf', at, bytes: r.size - at, complete: UNKNOWN };
        }
        /* '0' also opens `00dc` / `01wb` — the chunks of an AVI body whose RIFF
         * header went with the first cluster. */
        if (buf[i + 1] >= 0x30 && buf[i + 1] <= 0x39 && aviChunksAt(buf, i, n)) {
          const at = base + i;
          return { kind: 'payload', format: 'avi', at, bytes: r.size - at, complete: false };
        }
      } else if (a === A_START) {                           // 00 00 01 xx
        if (i >= 2 && buf[i - 1] === 0 && buf[i - 2] === 0) {
          const c = buf[i + 1], s = i - 2;
          if (c === 0xBA ? psPackAt(buf, s, n)
              : (((c >= 0xC0 && c <= 0xEF) || c === 0xBD) && pesAt(buf, s, n))) {
            const at = base + s;
            return { kind: 'container', format: 'mpeg-ps', at, bytes: r.size - at, complete: UNKNOWN };
          }
          if (loose) continue;
          if (c === 0xB3) {
            const g = mpegSeqAt(buf, s, n);
            if (g) loose = { kind: 'bitstream', format: 'mpeg-video', at: base + s, bytes: r.size - (base + s),
                             complete: false, width: g.width, height: g.height };
          } else if (c >= 0x20 && c <= 0x2F) {
            const g = m4vVolAt(buf, s, n);
            if (g) loose = { kind: 'bitstream', format: 'mpeg4', at: base + s, bytes: r.size - (base + s),
                             complete: false, width: g.width, height: g.height };
          } else if (c === 0x40 && buf[i + 2] === 0x01 && hevcParamSetsAt(buf, s, n)) {
            loose = { kind: 'bitstream', format: 'hevc', at: base + s, bytes: r.size - (base + s), complete: false };
          }
        }
      } else if (a === A_MXF) {                             // 06 0E 2B 34 → MXF
        if (buf[i + 1] === 0x0E && buf[i + 2] === 0x2B && buf[i + 3] === 0x34 && mxfAt(buf, i, n)) {
          const at = base + i;
          return { kind: 'container', format: 'mxf', at, bytes: r.size - at, complete: UNKNOWN };
        }
      } else if (a === A_FF) {                              // FF D8 FF → JPEG / MJPEG
        if (!loose && buf[i + 1] === 0xD8 && buf[i + 2] === 0xFF) {
          const g = jpegAt(buf, i, n);
          if (g) loose = { kind: 'bitstream', format: 'mjpeg', at: base + i, bytes: r.size - (base + i),
                           complete: false, width: g.width, height: g.height };
        }
      } else if (a === A_SYNC) {
        /* Transport stream first: 0x47 is one byte and worthless alone, but a
         * stream is a metronome — sync bytes exactly 188 apart. The second hit
         * is one compare and rejects 255 of every 256 candidates, so the eight-hit
         * confirmation runs vanishingly rarely.
         *
         * Not only 188: an AVCHD camcorder writes BDAV M2TS, where every packet
         * carries a 4-byte timestamp in front and the metronome beats every 192
         * bytes; DVB captures append 16 parity bytes and beat every 204. Until
         * 2026-09-25 only 188 was asked, so an M2TS with MPEG-2 inside (no H.264
         * SPS to fall back on) was reported empty — measured on a generated
         * `-mpegts_m2ts_mode 1` file. For 192 the container starts at the
         * timestamp, four bytes before the sync.
         *
         * Known and accepted gap: this needs seven periods of look-ahead, so a
         * sync landing in a slice's final ~1.4 KB is not confirmed there, and the
         * overlap (32 B) does not reach back that far. It costs nothing real —
         * a stream lays down a sync every packet, so any TS present offers
         * thousands of confirmable positions per megabyte and the ones in a tail
         * gap are simply confirmed a packet earlier. Written down rather than
         * left implicit: the undocumented hole in the OLD sampled probe is what
         * declared a 7.8 MB transport stream empty. */
        let tsAt = -1;
        for (const P of TS_PERIODS) {
          if (!(i + P * 7 < n && buf[i + P] === 0x47)) continue;
          let hits = 2;
          for (let k = 2; k < 8; k++) { if (buf[i + k * P] === 0x47) hits++; else break; }
          /* The metronome alone is not enough, and a real file proved it: a FAT
           * allocation table of 4-byte little-endian entries running through
           * 0x000A47xx lays down 0x47 every FOUR bytes, so every 188th byte is
           * one too — eight hits out of eight, on a buffer with no transport
           * stream in it. One spec field settles it for free:
           * adaptation_field_control (byte 3, bits 5-4) is 0 only in the value
           * ISO 13818-1 marks reserved, so a real packet never carries it while
           * that table carries nothing else. */
          if (hits < 6) continue;
          let real = 0;
          for (let k = 0; k < hits; k++) {
            const p3 = i + k * P + 3;
            if (p3 < n && ((buf[p3] >> 4) & 3) !== 0 && (buf[i + k * P + 1] & 0x80) === 0) real++;
          }
          if (real < hits - 1) continue;
          /* …and the field check is not enough either, measured 2026-09-25 when
           * the metronome gained 192 and 204: 188, 192 and 204 are all multiples
           * of four, so ANY junk with a short period — a monotonic FAT32 table
           * whose third byte passes through 0x47 (every base tried), a block
           * repeated every 64 bytes — puts the same bytes under every "packet",
           * and constant bytes satisfy any two fixed bit tests. What junk cannot
           * do is COUNT: the continuity_counter of a real stream steps by one
           * from packet to packet of the same PID. Repeated bytes repeat it. */
          if (!tsCountsUp(buf, i, P, n)) continue;
          tsAt = base + i - (P === 192 && base + i >= 4 ? 4 : 0);
          break;
        }
        if (tsAt >= 0) return { kind: 'container', format: 'mpegts', at: tsAt, bytes: r.size - tsAt, complete: UNKNOWN };
        /* Then a parameter set. `parseSpsGeometry` is the only test here that
         * junk cannot pass by accident, so an elementary stream is claimed on a
         * PARSED width and height, never on a count of NAL-shaped bytes. */
        if (!loose && i >= 4) {
          const isAnnexB = buf[i - 3] === 0 && buf[i - 2] === 0 && buf[i - 1] === 1;
          const nl = u32be(buf, i - 4);
          const isAvcc = nl >= 4 && nl <= 512;
          if ((isAnnexB || isAvcc) && IS_PROFILE[buf[i + 1]] && i + 72 < n) {
            const g = parseSpsGeometry(buf, i + 1);
            if (g) loose = { kind: 'bitstream', format: 'h264', at: 0, bytes: r.size,
                             complete: false, width: g.width, height: g.height };
          }
        }
      }
    }
    if (onProgress) onProgress(Math.min(1, (base + n) / r.size));
  }
  return loose;
}


/* ── MPEG audio: also periodicity, for the same reason as TS ─────────────────
 * An MP3 with no ID3 tag begins with a bare 11-bit frame sync, which is far too
 * short to scan for — but its frames chain, and a chain of four is something
 * junk does not produce. Worth the three reads: a stripped MP3 renamed .mp4 is
 * an intact file, and calling it empty is the failure this module prevents.
 */
async function findMpegAudio(r) {
  const BR = [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 0];
  const SR = [44100, 48000, 32000, 0];
  const frameLen = (b, o) => {
    if (b[o] !== 0xFF || (b[o + 1] & 0xE0) !== 0xE0) return 0;
    if (((b[o + 1] >> 3) & 3) !== 3 || ((b[o + 1] >> 1) & 3) !== 1) return 0;  // MPEG-1 Layer III
    const br = BR[(b[o + 2] >> 4) & 0xF], sr = SR[(b[o + 2] >> 2) & 3];
    if (!br || !sr) return 0;
    return Math.floor((144000 * br) / sr) + ((b[o + 2] >> 1) & 1);
  };
  for (const p of [0, Math.floor(r.size * 0.4)]) {
    const buf = await r.read(p, 64 << 10);
    for (let s = 0; s + 4 < buf.length && s < 8192; s++) {
      let pos = s, chain = 0;
      for (;;) {
        const L = frameLen(buf, pos);
        if (!L || pos + L + 4 > buf.length) break;
        chain++; pos += L;
        if (chain >= 4) break;
      }
      if (chain >= 4) return { kind: 'container', format: 'mp3', at: p + s,
                               bytes: r.size - (p + s), complete: p + s === 0 };
    }
  }
  return null;
}

/* ── "Empty" needs a proof, not a failed search ──────────────────────────────
 * A search can only ever say "nothing I know how to recognise". Printing that
 * as "there is no video in this file" was right only as long as the list of
 * things it recognises covered every form media takes — and it did not (see
 * the anchor table): for five weeks a healthy DVD .vob was told no program
 * could get video out of it. Growing the list closes the holes that are known;
 * it cannot close the ones that are not.
 *
 * So the definite verdict now stands on a POSITIVE finding about the bytes, and
 * the one finding that proves "no recording here" is repetition. Video is
 * compressed: every 512-byte sector of a recording is different from every
 * other. A file that is a handful of distinct sectors repeated over and over
 * cannot hold a video, whatever format anyone invents. That is exactly what the
 * cards hand back when the directory points at the wrong place:
 *
 *   Ksenia (2026-08-21)   66 MB = 1 536-byte FAT block ×43 457 → 3 distinct sectors
 *   bass24 (2026-08-11)   22 MB = 7 distinct high-entropy sectors in 1 MiB runs
 *                         (45 827 sectors, one of them repeated 36 864 times)
 *   erased flash / TRIM   all zeros or all 0xFF → 1 distinct sector
 *
 * Anything else the search could not place is `unknown`, and the caller treats
 * it as "we could not recognise it" — the repair still runs, because the engine
 * behind it (FFmpeg) knows formats this table does not.
 *
 * Cost: a sample of up to 64 windows × 32 KB decides; only a file that already
 * looks repetitive pays a second full pass, which it needs so the verdict can
 * name its numbers ("7 distinct blocks, repeated 45 827 times") rather than
 * state a conclusion. Sectors are compared as 32-bit words against the previous
 * sector first — in a filler file almost every sector equals its neighbour, so
 * the hash is paid once per run.
 */
const SECTOR = 512;
const FILLER_MAX_DISTINCT = 64;

function sectorHash(u32, w) {
  let h = 0x811C9DC5;
  for (let k = 0; k < 128; k++) { h ^= u32[w + k]; h = Math.imul(h, 16777619); }
  return h >>> 0;
}

/** What a repeated sector looks like — this picks the sentence the user reads. */
function describeSector(bytes) {
  let zero = true, ones = true;
  for (let k = 0; k < bytes.length; k++) {
    if (bytes[k] !== 0) zero = false;
    if (bytes[k] !== 0xFF) ones = false;
  }
  if (zero) return 'zeros';
  if (ones) return 'ones';
  /* An allocation table: little-endian cluster numbers that count up by one —
   * a contiguous file's chain, which is what a FAT holds and a card hands back
   * when the directory entry points into it. 16- or 32-bit entries. */
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let run32 = 0, run16 = 0;
  for (let k = 4; k + 4 <= bytes.length; k += 4) {
    const a = dv.getUint32(k - 4, true), b = dv.getUint32(k, true);
    run32 = (b === a + 1 && a > 1) ? run32 + 1 : 0;
    if (run32 >= 32) return 'fat';
  }
  for (let k = 2; k + 2 <= bytes.length; k += 2) {
    const a = dv.getUint16(k - 2, true), b = dv.getUint16(k, true);
    run16 = (b === a + 1 && a > 1) ? run16 + 1 : 0;
    if (run16 >= 64) return 'fat';
  }
  return 'repeat';
}

/**
 * Is this file a few sectors repeated? Returns `{filler:true, …numbers}` only on
 * proof; otherwise `{filler:false, sampled, distinct}` for the telemetry.
 */
export async function sectorCensus(file, opts) {
  const r = typeof file.read === 'function' ? file : toReader(file);
  const signal = opts && opts.signal;
  const total = Math.floor(r.size / SECTOR);
  if (total < 64) return { filler: false, sampled: 0, distinct: 0, sectors: total };

  // 1. Sample — windows spread from the first sector to the last.
  const WIN = 64;
  const wins = Math.max(1, Math.min(64, Math.floor(total / WIN)));
  const seen = new Set();
  let sampled = 0;
  for (let w = 0; w < wins; w++) {
    if (signal && signal.aborted) return null;
    const first = wins === 1 ? 0 : Math.floor(w * (total - WIN) / (wins - 1));
    const buf = await r.read(first * SECTOR, WIN * SECTOR);
    const u32 = new Uint32Array(buf.buffer, buf.byteOffset, buf.length >> 2);
    for (let s = 0; s + 128 <= u32.length; s += 128) { seen.add(sectorHash(u32, s)); sampled++; }
    if (seen.size > FILLER_MAX_DISTINCT) return { filler: false, sampled, distinct: seen.size, sectors: total };
  }
  if (seen.size * 32 > sampled) return { filler: false, sampled, distinct: seen.size, sectors: total };

  // 2. Looks repetitive: count every sector, so the verdict can show its numbers.
  //    Each sector is first compared word-for-word with the last few DISTINCT
  //    sectors seen (a ring of RING) — a run of one sector, or an allocation
  //    table cycling through three, then never hashes, allocates or touches the
  //    map. Measured on a 128 MB FAT pattern: 1.0 s → 0.1 s for this pass.
  const RING = 4;
  const ringW = new Uint32Array(RING * 128), ringH = new Uint32Array(RING), ringC = new Float64Array(RING);
  let ringN = 0, ringNext = 0;
  const counts = new Map(), sample = new Map();
  let counted = 0;
  const flush = (k) => { if (ringC[k]) { counts.set(ringH[k], (counts.get(ringH[k]) || 0) + ringC[k]); ringC[k] = 0; } };
  for (let at = 0; at < total * SECTOR; at += SLICE) {
    if (signal && signal.aborted) return null;
    const buf = await r.read(at, Math.min(SLICE, total * SECTOR - at));
    const u32 = new Uint32Array(buf.buffer, buf.byteOffset, buf.length >> 2);
    for (let s = 0; s + 128 <= u32.length; s += 128) {
      counted++;
      let hit = -1;
      for (let k = 0; k < ringN && hit < 0; k++) {
        const o = k * 128;
        let eq = true;
        for (let w = 0; w < 128; w++) if (u32[s + w] !== ringW[o + w]) { eq = false; break; }
        if (eq) hit = k;
      }
      if (hit >= 0) { ringC[hit]++; continue; }
      const h = sectorHash(u32, s);
      if (!sample.has(h)) {
        if (sample.size >= FILLER_MAX_DISTINCT) return { filler: false, sampled, distinct: sample.size + 1, sectors: total };
        sample.set(h, new Uint8Array(u32.slice(s, s + 128).buffer));
      }
      const slot = ringN < RING ? ringN++ : ringNext;
      if (ringN === RING) ringNext = (slot + 1) % RING;
      flush(slot);
      ringW.set(u32.subarray(s, s + 128), slot * 128);
      ringH[slot] = h;
      ringC[slot] = 1;
    }
  }
  for (let k = 0; k < ringN; k++) flush(k);
  const distinct = counts.size;
  if (distinct > FILLER_MAX_DISTINCT || distinct * 64 > counted) {
    return { filler: false, sampled, distinct, sectors: counted };
  }
  let topH = 0, top = 0;
  for (const [h, c] of counts) if (c > top) { top = c; topH = h; }
  const kinds = new Set();
  for (const bytes of sample.values()) kinds.add(describeSector(bytes));
  const kind = kinds.size === 1 ? [...kinds][0]
             : kinds.has('fat') ? 'fat'
             : (kinds.has('repeat') ? 'repeat' : 'blank');   // a mix of zeros and 0xFF
  return { filler: true, kind, distinct, sectors: counted, topShare: top / counted,
           topKind: describeSector(sample.get(topH)), sampled };
}

/* ── is this really a parameter set? ─────────────────────────────────────────
 * Used by the A_SYNC branch of the scan above, for the case where a file holds
 * H.264 straight off an encoder or carved out of an mdat with the box header
 * lost — no container anywhere.
 *
 * COUNTING NAL-shaped bytes does not work, and the measurement is what says so:
 * an allocation table whose cluster numbers pass through 0x000A0000 lays down a
 * `00 00 01` at every block boundary and produced 2730 "valid NAL headers" in
 * 4 MB across 10 distinct types — comfortably past any density or variety
 * threshold, on a buffer with no video in it whatsoever. A count of things that
 * look like headers is the same mistake as measuring entropy: it describes the
 * shape of the bytes instead of asking what they mean.
 *
 * So the claim is only made when a sequence parameter set actually PARSES into a
 * sane picture geometry. Random bits do not survive Exp-Golomb decoding into a
 * plausible width and height, which makes this the one test that cannot be
 * passed by accident.
 */

/** Bit reader over an RBSP, with H.264 emulation-prevention bytes removed. */
function rbspReader(b, from, max) {
  const out = [];
  for (let i = from, zeros = 0; i < b.length && out.length < max; i++) {
    if (zeros === 2 && b[i] === 3) { zeros = 0; continue; }   // 00 00 03 → 00 00
    zeros = b[i] === 0 ? zeros + 1 : 0;
    out.push(b[i]);
  }
  let bit = 0;
  const u1 = () => {
    const byte = out[bit >> 3];
    if (byte === undefined) throw new RangeError('eof');
    const v = (byte >> (7 - (bit & 7))) & 1;
    bit++;
    return v;
  };
  const u = (n) => { let v = 0; for (let i = 0; i < n; i++) v = (v << 1) | u1(); return v; };
  /** Exp-Golomb. A leading-zero run past 32 means these are not code words. */
  const ue = () => {
    let lz = 0;
    while (u1() === 0) if (++lz > 32) throw new RangeError('not exp-golomb');
    return lz ? ((1 << lz) - 1) + u(lz) : 0;
  };
  return { u1, u, ue };
}

const HIGH_PROFILES = [100, 110, 122, 244, 44, 83, 86, 118, 128, 138, 139, 134, 135];
const PROFILES = [66, 77, 88, ...HIGH_PROFILES];
/* Lookup tables, not `indexOf`: this is the first gate on every parameter-set
 * candidate the whole-file scan raises, and on junk it is the ONLY gate that
 * runs — so its cost is the cost of the feature. */
const IS_PROFILE = new Uint8Array(256), IS_HIGH = new Uint8Array(256);
for (const v of PROFILES) IS_PROFILE[v] = 1;
for (const v of HIGH_PROFILES) IS_HIGH[v] = 1;

/**
 * Parse an SPS far enough to read the picture size, or return null.
 * `body` starts at the byte AFTER the NAL header.
 */
function parseSpsGeometry(b, at) {
  try {
    const profile = b[at], level = b[at + 2];
    if (!IS_PROFILE[profile]) return null;              // one lookup, rejects ~94%
    if ((b[at + 1] & 0x03) !== 0) return null;          // reserved_zero_2bits
    if (level < 9 || level > 63) return null;
    const br = rbspReader(b, at + 3, 64);
    if (br.ue() > 31) return null;                      // seq_parameter_set_id
    if (IS_HIGH[profile]) {
      const chroma = br.ue();
      if (chroma > 3) return null;
      if (chroma === 3) br.u1();
      if (br.ue() > 6 || br.ue() > 6) return null;      // bit_depth_{luma,chroma}_minus8
      br.u1();                                          // qpprime_y_zero_transform_bypass
      if (br.u1()) return null;                         // scaling matrices — rare, not worth walking
    }
    if (br.ue() > 12) return null;                      // log2_max_frame_num_minus4
    const pocType = br.ue();
    if (pocType > 2) return null;
    if (pocType === 0) { if (br.ue() > 12) return null; }
    else if (pocType === 1) {
      br.u1(); br.ue(); br.ue();
      const n = br.ue();
      if (n > 255) return null;
      for (let i = 0; i < n; i++) br.ue();
    }
    if (br.ue() > 16) return null;                      // max_num_ref_frames
    br.u1();                                            // gaps_in_frame_num_allowed
    const wMbs = br.ue() + 1, hUnits = br.ue() + 1;
    const frameMbsOnly = br.u1();
    const width = wMbs * 16, height = hUnits * 16 * (frameMbsOnly ? 1 : 2);
    if (width < 16 || width > 16384 || height < 16 || height > 16384) return null;
    return { width, height, profile, level };
  } catch (_) { return null; }
}



/**
 * Locate the media payload in `file`.
 *
 * @param {File|Blob} file
 * @param {{onProgress?:(f:number)=>void, signal?:AbortSignal}} [opts]
 * `complete` is tri-state — true / false / null ("not determined"); see UNKNOWN.
 *
 * @returns {Promise<{kind:'container'|'mdat'|'payload'|'bitstream'|'none'|'unknown',
 *                    format:string|null, at:number, bytes:number, complete:boolean|null,
 *                    atoms:string[], ms:number, filler?:object, census?:object}>}
 *   `none`    — PROVEN empty: the file is a few sectors repeated (`filler` says which).
 *   `unknown` — searched every byte and recognised nothing, but the bytes do not
 *               prove emptiness either; the caller must still let the repair try.
 */
export async function locate(file, opts) {
  const o = opts || {};
  const r = toReader(file);
  const t0 = Date.now();
  /* `mdatAt` is reported whatever the headline `kind` is: callers that need the
   * payload position should not have to re-derive it from a verdict aimed at a
   * different question. -1 means "no mdat anywhere". */
  const done = (res, atoms) => {
    const out = { atoms: atoms || [], mdatAt: -1, mdatBytes: 0, ...res, ms: Date.now() - t0 };
    if (out.mdatAt < 0 && out.kind === 'mdat') { out.mdatAt = out.at; out.mdatBytes = out.bytes; }
    return out;
  };

  if (!r.size) return done({ kind: 'none', format: null, at: 0, bytes: 0, complete: false });

  // Tier 1 — an intact chain answers without touching the body.
  const walk = await walkBoxes(r, 0);
  /* An index is what separates "this plays" from "this is the untrunc case".
   * Reporting `[ftyp][mdat]` as a container would hand the caller a file that
   * every player refuses, labelled as the good outcome. */
  if (walk.ftypAt >= 0 && walk.hasIndex) {
    return done({ kind: 'container', format: 'mp4', at: walk.ftypAt,
                  bytes: r.size - walk.ftypAt, complete: !walk.truncated,
                  mdatAt: walk.mdat ? walk.mdat.at : -1,
                  mdatBytes: walk.mdat ? walk.mdat.bytes : 0 }, walk.seen);
  }
  if (walk.mdat) {
    return done({ kind: 'mdat', format: 'mp4', at: walk.mdat.at,
                  bytes: walk.mdat.bytes, complete: false }, walk.seen);
  }

  // Tier 2 — the chain is unusable; search every byte.
  const hit = await scanSignatures(r, o);
  if (hit) return done(hit, walk.seen);

  const mp3 = await findMpegAudio(r);
  if (mp3) return done(mp3, walk.seen);

  /* Nothing validated anywhere in a complete pass. That is NOT yet "empty" —
   * it is "nothing we recognise" (see sectorCensus). The definite verdict,
   * the one that ends the retry loop instead of feeding it, is given only when
   * the bytes themselves prove there is no recording in them. */
  const census = await sectorCensus(r, o);
  if (census && census.filler) {
    return done({ kind: 'none', format: null, at: 0, bytes: 0, complete: false, filler: census }, walk.seen);
  }
  return done({ kind: 'unknown', format: null, at: 0, bytes: 0, complete: false,
                census: census || null }, walk.seen);
}

if (typeof window !== 'undefined') window.MediaLocate = { locate, sectorCensus, version: '2.0.0' };
